import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { Chip } from '@material-ui/core';

const LINEAR_TEAM_ANNOTATION = 'linear.app/team-key';
const LINEAR_PROJECT_ANNOTATION = 'linear.app/project-id';

interface LinearIssue {
  id: string;
  identifier: string;
  title: string;
  state: {
    name: string;
    color: string;
  };
  priority: number;
  assignee?: {
    name: string;
  };
  url: string;
  createdAt: string;
}

const priorityLabels: Record<number, string> = {
  0: 'None',
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Low',
};

const columns: TableColumn<LinearIssue>[] = [
  {
    title: 'ID',
    field: 'identifier',
    width: '10%',
    render: (row: LinearIssue) => (
      <a href={row.url} target="_blank" rel="noopener noreferrer">
        {row.identifier}
      </a>
    ),
  },
  {
    title: 'Title',
    field: 'title',
    width: '40%',
  },
  {
    title: 'Status',
    field: 'state.name',
    width: '15%',
    render: (row: LinearIssue) => (
      <Chip
        label={row.state.name}
        size="small"
        style={{
          backgroundColor: row.state.color,
          color: '#fff',
        }}
      />
    ),
  },
  {
    title: 'Priority',
    field: 'priority',
    width: '10%',
    render: (row: LinearIssue) => priorityLabels[row.priority] ?? 'Unknown',
  },
  {
    title: 'Assignee',
    field: 'assignee.name',
    width: '15%',
    render: (row: LinearIssue) => row.assignee?.name ?? 'Unassigned',
  },
];

export const isLinearAvailable = (entity: { metadata: { annotations?: Record<string, string> } }) =>
  Boolean(
    entity.metadata.annotations?.[LINEAR_TEAM_ANNOTATION] ||
    entity.metadata.annotations?.[LINEAR_PROJECT_ANNOTATION],
  );

export const EntityLinearIssuesCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const teamKey = entity.metadata.annotations?.[LINEAR_TEAM_ANNOTATION];
  const projectId = entity.metadata.annotations?.[LINEAR_PROJECT_ANNOTATION];

  const { value, loading, error } = useAsync(async () => {
    const proxyUrl = await discoveryApi.getBaseUrl('proxy');

    let query: string;
    let variables: Record<string, unknown>;

    if (projectId) {
      query = `
        query ProjectIssues($projectId: String!) {
          issues(filter: { project: { id: { eq: $projectId } }, state: { type: { nin: ["completed", "canceled"] } } }, first: 25, orderBy: updatedAt) {
            nodes {
              id identifier title url createdAt
              priority
              state { name color }
              assignee { name }
            }
          }
        }
      `;
      variables = { projectId };
    } else {
      query = `
        query TeamIssues($teamKey: String!) {
          issues(filter: { team: { key: { eq: $teamKey } }, state: { type: { nin: ["completed", "canceled"] } } }, first: 25, orderBy: updatedAt) {
            nodes {
              id identifier title url createdAt
              priority
              state { name color }
              assignee { name }
            }
          }
        }
      `;
      variables = { teamKey };
    }

    const response = await fetchApi.fetch(`${proxyUrl}/linear/api/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Linear API responded with ${response.status}`);
    }

    const data = await response.json();
    if (data.errors) {
      throw new Error(data.errors[0]?.message ?? 'Linear GraphQL error');
    }

    return data.data.issues.nodes as LinearIssue[];
  }, [teamKey, projectId]);

  if (loading) {
    return (
      <InfoCard title="Linear Issues">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <Table
      title={`Linear Issues${teamKey ? ` (${teamKey})` : ''}`}
      options={{ paging: true, pageSize: 10, search: true }}
      columns={columns}
      data={value ?? []}
    />
  );
};
